import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'inwards' },

    // Redirect signed in user to the '/example'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'inwards' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [

            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.module').then(
                        (m) => m.AuthSignUpModule
                    ),
            },
            {
                path: 'otp-validation',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required-otp/confirmation-required-otp.module'
                    ).then((m) => m.AuthConfirmationOtpRequiredModule),
            },
            {
                path: 'mfa-validation',
                loadChildren: () =>
                    import(
                        'app/modules/auth/mfa-validation/mfa-validation.module'
                    ).then((m) => m.AuthMfaValidationModule),
            }
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.module'
                    ).then((m) => m.AuthConfirmationRequiredModule),
            }

        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule
                    ),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'inwards',
                loadChildren: () =>
                    import(
                        './modules/admin/transactions/inwards/inwards.module'
                    ).then((m) => m.InwardsModule),
            },
            {

                path: 'dashboard',
                loadChildren: () =>
                    import('app/modules/admin/dashboard/dashboard.module').then(
                        (m) => m.DashboardModule
                    )
            },
            {
                path: 'chat',
                loadChildren: () =>
                    import('app/modules/admin/chat/chat.module').then(
                        (m) => m.ChatModule
                    ),
            },
            {
                path: 'tasks',
                loadChildren: () =>
                    import('app/modules/admin/tasks/tasks.module').then(
                        (m) => m.TasksModule
                    ),
            },
            {
                path: 'usersettings',
                loadChildren: () =>
                    import('app/modules/admin/users/user-settings/user-settings.module').then(m => m.UserSettingsModule
                    )

            },
            {
                path: 'masters',
                children: [
                    {
                        path: 'banks',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/banks/banks.module'
                            ).then((m) => m.BanksModule),
                    },
                    {
                        path: 'bankbranches',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/bankbranches/bankbranches.module'
                            ).then((m) => m.BankBranchesModule),
                    },
                    {
                        path: 'zones',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/zone/zone.module'
                            ).then((m) => m.ZoneModule),
                    },
                    {
                        path: 'teams',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/teams/teams.module'
                            ).then((m) => m.TeamsModule),
                    },
                    {
                        path: 'categories',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/categories/categories.module'
                            ).then((m) => m.CategoriesModule),
                    },
                    {
                        path: 'employees',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/employees/employees.module'
                            ).then((m) => m.EmployeesModule),
                    },
                    {
                        path: 'consultants',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/consultants/consultants.module'
                            ).then((m) => m.ConsultantsModule),
                    },
                    {
                        path: 'areas',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/areas/areas.module'
                            ).then((m) => m.AreasModule),
                    },
                    {
                        path: 'optiontypes',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/optiontypes/optionTypes.module'
                            ).then((m) => m.OptionTypesModule),
                    },
                    {
                        path: 'remarks',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/remarks/remarks.module'
                            ).then((m) => m.RemarksModule),
                    },
                    {
                        path: 'bankaccountdetails',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/bankAccountDetails/bankAccountDetails.module'
                            ).then((m) => m.BankAccountDetailsModule),
                    },
                    {
                        path: 'options',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/options/options.module'
                            ).then((m) => m.OptionsModule),
                    },
                    {
                        path: 'loandepartments',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/loandepartments/loandepartments.module'
                            ).then((m) => m.LoanDepartmentsModule),
                    },
                    {
                        path: 'templates',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/templates/templates.module'
                            ).then((m) => m.TemplatesModule),
                    },
                    {
                        path: 'cities',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/city/city.module'
                            ).then((m) => m.CityModule),
                    },
                    {
                        path: 'expenses',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/expenses/expenses.module'
                            ).then((m) => m.ExpensesModule),
                    },
                    {
                        path: 'expensesCategories',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/expenseCategories/expenseCategories.module'
                            ).then((m) => m.ExpenseCategoriesModule),
                    },
                    {
                        path: 'roles',
                        loadChildren: () =>
                            import(
                                './modules/admin/masters/role-menu/role-menu.module'
                            ).then((m) => m.RoleMenuModule),
                    }

                ],
            },
            {
                path: 'transactions',
                children: [
                    {
                        path: 'inwards',
                        loadChildren: () =>
                            import(
                                './modules/admin/transactions/inwards/inwards.module'
                            ).then((m) => m.InwardsModule),
                    },
                    {
                        path: 'invoices',
                        loadChildren: () =>
                            import(
                                './modules/admin/transactions/invoices/invoices.module'
                            ).then((m) => m.InvoicesModule),
                    },
                    {
                        path: 'payments',
                        loadChildren: () =>
                            import(
                                './modules/admin/transactions/payments/payments.module'
                            ).then((m) => m.PaymentsModule),
                    }
                ]
            },
            {
                path: 'reports',
                children: [
                    {
                        path: 'searchValues',
                        loadChildren: () =>
                            import(
                                './modules/admin/reports/searchvalues/searchvalues.module'
                            ).then((m) => m.SearchValuesModule),
                    },
                    {
                        path: 'reportRegister',
                        loadChildren: () =>
                            import(
                                './modules/admin/reports/reportregister/reportregister.module'
                            ).then((m) => m.ReportRegisterModule),
                    },
                    {
                        path: 'bankWiseOutwardSummary',
                        loadChildren: () =>
                        import(
                            './modules/admin/reports/bankwiseoutwardsummary/bankwiseoutwardsummary.module'
                        ).then((m) => m.BankWiseOutwardSummaryModule),
                    },
                    {
                        path: 'reportstatushistory',
                        loadChildren: () =>
                        import(
                            './modules/admin/reports/reportStatusHistory/reportStatusHistory.module'
                        ).then((m) => m.ReportStatusHistoryModule),
                    },
                    {
                        path: 'previousValuations',
                        loadChildren: () =>
                        import(
                            './modules/admin/reports/previousvaluations/previous-valuations.module'
                        ).then((m) => m.PreviousValuationsModule),
                    }
                ]
            }
        ],
    },
];
